// calls all apis for ya

import Axios from 'axios';
import moment from 'moment';
import { get, isEmpty, isEqual } from 'lodash';

import rootStore from '../store/store';
import { apiBaseUrl, constants } from '../constants/configuration';
import {
  defferedPingServerInSeconds,
  internetDisconnectivityHandledAPIEndpointBlacklist,
  internetErrorResponseStoreDispatchDelay,
  serverErrorCodes,
  storageTypes,
  tokenExcludedAPIEndpointBlacklist,
  tokenRefreshBufferMins,
} from '../constants/globals';
import { apiErrorResponseAction } from '../actions/app.action';
import { refreshTokenAction, updateAuthTokensAction } from '../actions/authTokens';
import {
  checkInternetConnectivity,
  getIsApiRequestOnHold,
  isAccessDenied403Error,
  isAuth401Error,
  getEnableProactiveTokenRefresh,
  isJWTTokenExpired,
  isJWTTokenExpiringSoon,
  isNativeWebView,
  setIsApiRequestOnHold,
} from './helpers';

import { getItem, setItem } from './storage';
import { differenceBetween } from './fudrLocalizationHelper';
import resourcesConfig from '../resources';

const { store, persistor } = rootStore;

const TYPE_JSON = 'application/json';
const NATIVE_TOKEN_REFRESH_TIMEOUT_MS = 15000;

let pendingRequests = [];
let refreshPromise = null;
let nativeRefreshPromise = null;
let nativeRefreshResolve = null;
let nativeRefreshReject = null;
let nativeRefreshTimeoutId = null;

export const onNativeTokenRefreshed = (newToken) => {
  if (nativeRefreshTimeoutId) {
    clearTimeout(nativeRefreshTimeoutId);
    nativeRefreshTimeoutId = null;
  }
  if (nativeRefreshResolve) {
    nativeRefreshResolve(newToken);
  }
  nativeRefreshResolve = null;
  nativeRefreshReject = null;
  nativeRefreshPromise = null;
};

export const onNativeTokenRefreshFailed = (error = new Error('Native refresh failed')) => {
  if (nativeRefreshTimeoutId) {
    clearTimeout(nativeRefreshTimeoutId);
    nativeRefreshTimeoutId = null;
  }
  if (nativeRefreshReject) {
    nativeRefreshReject(error);
  }
  nativeRefreshResolve = null;
  nativeRefreshReject = null;
  nativeRefreshPromise = null;
};

const getStoredAccessToken = () => {
  const authData =
    getItem(storageTypes.localStorage, 'AuthData') ||
    get(store && store.getState(), 'authentication.authData', {});
  return get(authData, 'accessToken', '');
};

const refreshWebTokens = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const storedState = store && store.getState();
      const authData =
        getItem(storageTypes.localStorage, 'AuthData') ||
        get(storedState, 'authentication.authData', {});
      const refreshToken = get(authData, 'refreshToken', '');

      if (!refreshToken || isJWTTokenExpired(refreshToken)) {
        return { isError: true, terminal: true };
      }

      if (!store) {
        return { isError: true, error: new Error('Redux store is not initialized'), terminal: false };
      }

      const res = await store.dispatch(refreshTokenAction());
      const nextAccessToken = get(res, 'accessToken', '');
      const nextRefreshToken = get(res, 'refreshToken', '');

      if (!nextAccessToken || !nextRefreshToken) {
        const status = get(res, 'error.response.status', null);
        const terminal = status === 401 || status === 403;
        return { isError: true, error: get(res, 'error'), terminal };
      }

      await store.dispatch(
        updateAuthTokensAction({
          accessToken: nextAccessToken,
          refreshToken: nextRefreshToken,
        }),
      );

      return { accessToken: nextAccessToken, refreshToken: nextRefreshToken };
    } catch (e) {
      const status = get(e, 'response.status', null);
      const terminal = status === 401 || status === 403;
      return { isError: true, error: e, terminal };
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const requestNativeTokens = async () => {
  if (nativeRefreshPromise) return nativeRefreshPromise;

  nativeRefreshPromise = new Promise((resolve, reject) => {
    nativeRefreshResolve = resolve;
    nativeRefreshReject = reject;
  });

  nativeRefreshTimeoutId = setTimeout(() => {
    onNativeTokenRefreshFailed(new Error('Native token refresh timed out'));
  }, NATIVE_TOKEN_REFRESH_TIMEOUT_MS);

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      eventType: { type: 'native-token-refresh-request' },
    }),
  );

  return nativeRefreshPromise;
};

let sessionBoundaryRefreshPromise = null;
let sessionBoundaryListenersRegistered = false;

export const attemptSessionBoundaryTokenRefresh = async () => {
  // Native app owns the shared session; proactive refresh runs there only.
  if (isNativeWebView) return;

  const accessToken = getStoredAccessToken();
  if (isEmpty(accessToken)) return;

  const isExpired = isJWTTokenExpired(accessToken);
  const isExpiringSoon =
    getEnableProactiveTokenRefresh() &&
    isJWTTokenExpiringSoon(accessToken, tokenRefreshBufferMins);

  if (!isExpired && !isExpiringSoon) return;

  if (sessionBoundaryRefreshPromise) return sessionBoundaryRefreshPromise;

  sessionBoundaryRefreshPromise = (async () => {
    try {
      const res = await refreshWebTokens();
      if (get(res, 'isError', false)) {
        console.error('[auth:refreshToken] session boundary refresh failed', {
          status: get(res, 'error.response.status', null),
          terminal: res.terminal,
        });
      }
    } catch (e) {
      console.error('[auth:refreshToken] session boundary refresh threw', e);
    }
  })().finally(() => {
    sessionBoundaryRefreshPromise = null;
  });

  return sessionBoundaryRefreshPromise;
};

export const buildAPIInstance = ({ contentType = TYPE_JSON }) => {
  const contextualTimeStamp = moment().format();

  const nativeAppConfig = window.nativeAppConfig;
  const appName = get(nativeAppConfig, 'appName');
  const appId = get(nativeAppConfig, 'appId');
  const sourceType = get(nativeAppConfig, 'sourceType', 'client');

  const headers = {
    common: {
      Accept: TYPE_JSON,
      'Content-Type': contentType,
    },
    post: {
      'Content-Type': TYPE_JSON,
    },
  };

  headers.common['x-source-type'] = sourceType;
  if (!isEmpty(appName)) {
    headers.common['x-app-name'] = appName;
  }
  if (!isEmpty(appId)) {
    headers.common['x-app-id'] = appId;
  }

  const objAxios = Axios.create({
    baseURL: apiBaseUrl,
    headers,
  });

  const shouldSkipAuthHeader = (url) =>
    tokenExcludedAPIEndpointBlacklist.some((endpoint) => String(url || '').includes(endpoint));

  const enqueueRequestWhileOnHold = (config, fromResponse = false) =>
    new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject, config, fromResponse, axiosInstance: objAxios });
    });

  const getActiveAccessToken = () => getStoredAccessToken();

  const handlePendingRequests = (params) => {
    const newToken = params && params.newToken;
    const error = params && params.error;
    if (newToken) {
      for (const request of pendingRequests) {
        try {
          if (typeof request.resolve !== 'function' || !request.config) {
            console.warn('Skipped malformed pending request (resolve):', request);
            continue;
          }
          if (!request.config.headers) {
            request.config.headers = {};
          }
          request.config.headers['Authorization'] = `Bearer ${newToken}`;
          if (request.fromResponse) {
            const retryAxios = request.axiosInstance || objAxios;
            retryAxios(request.config).then(request.resolve).catch(request.reject);
          } else {
            request.resolve(request.config);
          }
        } catch (err) {
          console.error('Failed to resolve pending request:', err, request);
        }
      }
    } else {
      for (const request of pendingRequests) {
        try {
          if (typeof request.reject === 'function') {
            request.reject(error);
          } else {
            console.warn('Skipped malformed pending request (reject):', request);
          }
        } catch (err) {
          console.error('Failed to reject pending request:', err, request);
        }
      }
    }

    pendingRequests = [];
  };

  const handleLogoutError = (error) => {
    let errorData = {};
    try {
      const statusCode = get(error, 'response.status', '');
      const responseData = get(error, 'response.data', error);
      if (responseData && typeof responseData === 'object') {
        errorData = { ...responseData };
      } else {
        errorData = { message: responseData };
      }
      errorData.statusCode = statusCode || get(error, 'response.statusCode', '');
    } catch (err) {
      errorData = { message: get(error, 'response.data', error) };
    }
    if (!errorData || isEmpty(errorData)) {
      console.error('No error data found in error object:', error);
      return;
    }

    try {
      if (persistor.getState().bootstrapped) {
        store.dispatch(apiErrorResponseAction(errorData));
      } else {
        const unsubscribe = persistor.subscribe(() => {
          if (persistor.getState().bootstrapped) {
            unsubscribe();
            store.dispatch(apiErrorResponseAction(errorData));
          }
        });
      }
    } catch (err) {
      store.dispatch(apiErrorResponseAction(errorData));
    }
  };

  const handleTokenRefresh = refreshWebTokens;

  const handleNativeAuthError = (error) => {
    handlePendingRequests({ error });
    handleLogoutError(error);
    return Promise.reject(error);
  };

  const requestNativeTokenRefresh = requestNativeTokens;

  const handleAuthenticationError = async (error) => {
    if (isNativeWebView) {
      setIsApiRequestOnHold(true);
      try {
        const newToken = await requestNativeTokenRefresh();
        if (!newToken) {
          throw new Error('Native refresh did not return token');
        }
        if (store) {
          await store.dispatch(updateAuthTokensAction({ accessToken: newToken }));
        }
        setIsApiRequestOnHold(false);
        handlePendingRequests({ newToken });
        return newToken;
      } catch (nativeRefreshError) {
        console.error('Native refresh error', nativeRefreshError);
        setIsApiRequestOnHold(false);
        return handleNativeAuthError(nativeRefreshError || error);
      }
    }

    setIsApiRequestOnHold(true);

    try {
      const tokenRes = await handleTokenRefresh();
      const newToken = get(tokenRes, 'accessToken', '');

      if (newToken) {
        setIsApiRequestOnHold(false);
        handlePendingRequests({ newToken });
        return newToken;
      }

      const err = get(tokenRes, 'error', error);
      setIsApiRequestOnHold(false);
      handlePendingRequests({ error: err || error });
      handleLogoutError({
        response: {
          data: {
            isError: true,
            code: serverErrorCodes.invalidToken,
            message: 'Authentication error',
            detail: 'Authentication error',
            status: 'UNAUTHORIZED',
            statusCode: 401,
          },
          status: 401,
        },
      });
      return null;
    } finally {
      setIsApiRequestOnHold(false);
    }
  };

  objAxios.interceptors.request.use(async (config) => {
    if (!config.url) {
      return config;
    }

    const isApiRequestOnHold = getIsApiRequestOnHold();
    if (isApiRequestOnHold && !shouldSkipAuthHeader(config.url)) {
      return enqueueRequestWhileOnHold(config);
    }

    const accessToken = getActiveAccessToken();

    // Running session: always attach + hit API. Auth handling only on response (refresh → dispatch).
    if (!isEmpty(accessToken) && !shouldSkipAuthHeader(config.url)) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const isExternalLoyaltyEnabled = get(
      store.getState(),
      'common.resources.isExternalLoyaltyEnabled',
      resourcesConfig.isExternalLoyaltyEnabled,
    );
    if (isExternalLoyaltyEnabled) {
      config.headers['x-ext-loyalty'] = 'xeno';
    }

    const geoLocation = localStorage.getItem('geoLocation');
    if (geoLocation) {
      config.headers.common['location'] = geoLocation;
    }

    if (!isNativeWebView) {
      const requestURLPart = config.url;
      const isRequestURLBlacklisted = internetDisconnectivityHandledAPIEndpointBlacklist.some(
        (endpoint) => requestURLPart.indexOf(endpoint) > -1,
      );
      if (!isRequestURLBlacklisted) {
        const currentTimeStamp = getItem(storageTypes.sessionStorage, 'currentTimeStamp');
        const differenceInTimeStamps = differenceBetween(
          contextualTimeStamp,
          currentTimeStamp,
          'seconds',
        );
        if (differenceInTimeStamps > defferedPingServerInSeconds) {
          setItem(storageTypes.sessionStorage, 'currentTimeStamp', moment().format());
          checkInternetConnectivity().then((isOnline) => {
            if (!isOnline) {
              const errorResponse = {
                message: constants.globalErrorInfo.internetDisconnectivityErrorMessage,
              };
              console.error('Internet error response:', errorResponse);
              setTimeout(() => {
                store.dispatch(apiErrorResponseAction(errorResponse));
              }, internetErrorResponseStoreDispatchDelay);
            }
          });
        }
      }
    }

    return config;
  });

  objAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const errorData = get(error, 'response.data', {});
      const errorStatus = get(error, 'response.status', '');
      const normalizedError = {
        ...errorData,
        statusCode: errorStatus || get(errorData, 'statusCode', ''),
      };

      if (isNativeWebView) {
        if (isAccessDenied403Error(normalizedError) || isAuth401Error(normalizedError)) {
          if (originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            const isApiRequestOnHold = getIsApiRequestOnHold();
            if (isApiRequestOnHold) {
              return enqueueRequestWhileOnHold(originalRequest, true);
            }

            const newToken = await handleAuthenticationError(error);
            if (newToken && originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return objAxios(originalRequest);
            }
          }
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }

      const isInvalidTokenCode = isEqual(
        get(error, 'response.data.code'),
        serverErrorCodes.invalidToken,
      );
      const isUnauthorized = isEqual(get(error, 'response.status'), 401);

      if ((isInvalidTokenCode || isUnauthorized) && shouldSkipAuthHeader(get(originalRequest, 'url', ''))) {
        return Promise.reject(error);
      }

      if ((isInvalidTokenCode || isUnauthorized) && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        const isApiRequestOnHold = getIsApiRequestOnHold();
        if (isApiRequestOnHold) {
          return enqueueRequestWhileOnHold(originalRequest, true);
        }

        const newToken = await handleAuthenticationError(error);
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return objAxios(originalRequest);
        }
      }

      return Promise.reject(error);
    },
  );

  return objAxios;
};

const client = (params = {}) => buildAPIInstance(params);

export default client;
